/**
 * Réglages › Invitations: what rides along with every invitation.
 *
 * The e-mail announce address (offices only) and the règlement intérieur —
 * two things a manager sets once and every invitation then carries.
 */
import type { RichTextDoc } from "@gately/validators";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy, Mail, RefreshCw } from "lucide-react";

import { Button } from "@gately/ui/button";
import { ConfirmDialog } from "@gately/ui/confirm-dialog";
import { Input } from "@gately/ui/input";
import { RichTextEditor } from "@gately/ui/rich-text-editor";
import { SettingsSection } from "@gately/ui/settings";
import { Switch } from "@gately/ui/switch";
import { toast } from "@gately/ui/toast";
import { isRichTextEmpty } from "@gately/validators";

import { orpc } from "~/lib/orpc";
import { activeMembership, useMe } from "~/lib/use-me";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/reglages/invitations")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
  component: InvitationsTab,
});

function InvitationsTab() {
  const queryClient = useQueryClient();
  const me = useMe();
  const terms = useTerms();

  return (
    <div className="space-y-8">
      <InboundEmailSection
        key={`inbound-email-${me.activeOrg?.id ?? "no-org"}`}
      />

      <ReglementSection
        key={me.activeOrg?.id ?? "no-org"}
        activeOrg={me.activeOrg}
        theOrg={terms.theOrg}
        queryClient={queryClient}
      />
    </div>
  );
}

function InboundEmailSection() {
  const me = useMe();
  const qc = useQueryClient();
  const isOffice = activeMembership(me)?.orgType === "office";
  const settings = useQuery({
    ...orpc.inboundEmail.settings.queryOptions(),
    enabled: isOffice,
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: orpc.inboundEmail.settings.key() });
  const ensure = useMutation(
    orpc.inboundEmail.ensureAddress.mutationOptions({
      onSuccess: invalidate,
      onError: () => toast.error("Impossible de créer l'adresse."),
    }),
  );
  const regenerate = useMutation(
    orpc.inboundEmail.regenerateAddress.mutationOptions({
      onSuccess: () => {
        toast.success(
          "Nouvelle adresse générée. L'ancienne ne fonctionne plus.",
        );
        invalidate();
        setConfirmOpen(false);
      },
      onError: () => {
        toast.error("Impossible de régénérer l'adresse.");
        setConfirmOpen(false);
      },
    }),
  );

  if (!isOffice || !settings.data || !settings.data.enabled) return null;
  const d = settings.data;

  const copy = async () => {
    if (!d.address) return;
    try {
      await navigator.clipboard.writeText(d.address);
      toast.success("Adresse copiée.");
    } catch {
      toast.error(
        "Impossible de copier l'adresse. Sélectionnez-la et copiez-la manuellement.",
      );
    }
  };
  return (
    <SettingsSection
      title="Annonce de visites par e-mail"
      hint="Les employés mettent cette adresse en copie de leur e-mail ou l'ajoutent comme invité de la réunion : les visiteurs en copie reçoivent leur pass automatiquement."
    >
      {d.address ? (
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={d.address}
            className="font-mono text-sm"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="outline"
            onClick={() => void copy()}
            aria-label="Copier l'adresse"
          >
            <Copy className="size-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmOpen(true)}
            disabled={regenerate.isPending}
            aria-label="Régénérer l'adresse"
          >
            <RefreshCw className="size-4" />
          </Button>
        </div>
      ) : (
        <Button
          onClick={() => ensure.mutate(undefined)}
          disabled={ensure.isPending}
        >
          <Mail className="mr-2 size-4" /> Créer l'adresse
        </Button>
      )}

      <p className="text-xs text-neutral-500">
        Envoyez l'invitation sans pièce jointe volumineuse (limite 150 Ko).
        Seuls les e-mails d'employés enregistrés sont pris en compte.
      </p>

      <div className="grid grid-cols-4 divide-x rounded-lg border text-center">
        {(
          [
            ["Reçus", d.counters.received],
            ["Visites créées", d.counters.created],
            ["Mises à jour / annulées", d.counters.updatedOrCancelled],
            ["Rejetés", d.counters.rejected],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="p-3">
            <div className="text-lg font-semibold">{value}</div>
            <div className="text-xs text-neutral-500">{label}</div>
          </div>
        ))}
      </div>

      {d.unknownSenders.length > 0 ? (
        <div className="space-y-1">
          <h3 className="text-sm font-medium">
            Expéditeurs inconnus (30 jours)
          </h3>
          <p className="text-xs text-neutral-500">
            Ces adresses ont écrit sans être enregistrées comme employés.
            Ajoutez-les à l'équipe de leur entreprise pour que leurs e-mails
            soient pris en compte.
          </p>
          <div className="divide-y rounded-lg border">
            {d.unknownSenders.map((u, i) => (
              <div
                key={`${u.masked}-${i}`}
                className="flex items-center justify-between p-3 text-sm"
              >
                <span className="font-mono">{u.masked}</span>
                <span className="text-neutral-500">
                  {u.count} e-mail{u.count > 1 ? "s" : ""}
                  {u.lastAt
                    ? ` · ${new Date(u.lastAt).toLocaleDateString("fr-FR")}`
                    : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Régénérer l'adresse ?"
        description="L'ancienne adresse cessera de fonctionner immédiatement et devra être communiquée à nouveau aux employés."
        confirmLabel="Régénérer"
        pendingLabel="Régénération…"
        destructive
        pending={regenerate.isPending}
        onConfirm={() => regenerate.mutate(undefined)}
      />
    </SettingsSection>
  );
}

type ActiveOrg = {
  reglementInterieur: RichTextDoc | null;
  reglementEnabled: boolean;
} | null;

function ReglementSection({
  activeOrg,
  theOrg,
  queryClient,
}: {
  activeOrg: ActiveOrg;
  /** "la résidence" or "l'immeuble", per the vertical (`getTerms(...).theOrg`). */
  theOrg: string;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  // Initialised from activeOrg on mount. The parent passes key={activeOrg?.id}
  // so this component remounts when the org changes, re-seeding from fresh data.
  const [reglementDoc, setReglementDoc] = useState<RichTextDoc | null>(
    activeOrg?.reglementInterieur ?? null,
  );
  const [reglementOn, setReglementOn] = useState(
    activeOrg?.reglementEnabled ?? false,
  );

  const saveReglement = useMutation(
    orpc.organization.setReglement.mutationOptions({
      onSuccess: () => {
        toast.success("Règlement intérieur enregistré.");
        void queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );

  return (
    <SettingsSection
      title="Règlement intérieur"
      hint={`Texte optionnel joint à chaque invitation. En entrant dans ${theOrg}, le visiteur en accepte implicitement les termes.`}
    >
      {/* NOT a SettingRow: the label WRAPS the Switch instead of pointing at it
          with htmlFor, which is what makes the whole line a click target. */}
      <label className="flex items-center gap-3 text-sm">
        <Switch checked={reglementOn} onCheckedChange={setReglementOn} />
        Joindre le règlement intérieur à chaque invitation
      </label>

      <RichTextEditor value={reglementDoc} onChange={setReglementDoc} />

      <Button
        onClick={() =>
          saveReglement.mutate({
            doc:
              reglementDoc && !isRichTextEmpty(reglementDoc)
                ? reglementDoc
                : null,
            enabled: reglementOn,
          })
        }
        disabled={saveReglement.isPending}
      >
        Enregistrer le règlement
      </Button>
    </SettingsSection>
  );
}
